import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { AlertTriangle, Save } from "lucide-react";
import { useAuth } from "../context/AuthContext";

// Edição CONTEXTUAL do cadastro do cliente, por cima do fluxo de
// faturamento. Existe porque a emissão falha quando faltam dados do
// cliente (ex.: "Complete no cadastro do cliente: CPF/CNPJ") e, até aqui,
// o atalho "Editar cliente" abria a tela de Clientes em outra aba — o
// usuário corrigia o cadastro e voltava para um modal de pagamento que já
// tinha perdido tudo (forma mista, valores, parcelas, vencimentos).
//
// Este diálogo é montado DENTRO do modal de pagamento, como irmão do
// conteúdo dele: o componente do pagamento nunca desmonta, então o estado
// já preenchido fica intacto por construção — nada precisa ser
// serializado, salvo ou reconstruído.
//
// NÃO é a tela de Clientes duplicada: edita só os campos que a cobrança
// exige, usando o MESMO endpoint oficial (PUT /clients/{id}) e as MESMAS
// regras do backend, que segue sendo a única autoridade.

// Campos que a cobrança pode exigir. `rotulos` são exatamente os termos
// que os validadores do backend usam na mensagem de erro
// (_operational_payer_data / _asaas_customer_payload / cobranca-requisitos)
// — é só a tradução "o que falta" -> "qual input destacar", nunca uma
// segunda lista de regras.
const CAMPOS = [
  { campo: "razao_social", label: "Nome / Razão social", rotulos: ["nome/razão social", "razão social"] },
  { campo: "cpf_cnpj", label: "CPF/CNPJ", rotulos: ["cpf/cnpj"] },
  { campo: "email", label: "E-mail", rotulos: ["e-mail", "email"] },
  { campo: "telefone", label: "Telefone", rotulos: ["telefone"] },
  { campo: "cep", label: "CEP", rotulos: ["cep"] },
  { campo: "endereco", label: "Endereço", rotulos: ["endereço", "endereco"] },
  { campo: "numero", label: "Número", rotulos: ["número", "numero"] },
  { campo: "bairro", label: "Bairro", rotulos: ["bairro"] },
  { campo: "cidade", label: "Cidade", rotulos: ["cidade"] },
  { campo: "estado", label: "UF", rotulos: ["uf", "estado"] },
];

/**
 * Descobre, a partir da mensagem do backend, QUAIS campos do cadastro
 * precisam ser destacados. Funciona para as duas origens — a validação
 * prévia (GET /clients/{id}/cobranca-requisitos) e o erro 400 da emissão
 * real — porque as duas usam os mesmos rótulos. Mensagem desconhecida ->
 * nenhum destaque (o diálogo abre normal, sem inventar campo).
 */
export function camposFaltantesDaMensagem(mensagem) {
  const texto = (mensagem || "").toLowerCase();
  if (!texto) return [];
  return CAMPOS.filter(({ rotulos }) => rotulos.some((r) => texto.includes(r))).map((c) => c.campo);
}

export default function ClienteEditDialog({ open, clienteId, mensagem = "", onOpenChange, onSaved }) {
  // `useAuth()` pode vir sem contexto (ex.: o componente renderizado
  // isolado em teste de unidade de outro diálogo). Sem o catálogo de
  // permissões em mãos, o formulário é exibido e o BACKEND continua sendo
  // a autoridade: PUT /clients/{id} exige clients:edit e recusa 403.
  const { hasPerm } = useAuth() || {};
  const podeEditar = hasPerm ? hasPerm("clients:edit") : true;
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const primeiroFaltanteRef = useRef(null);

  const faltantes = camposFaltantesDaMensagem(mensagem);
  const faltante = (campo) => faltantes.includes(campo);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clienteId}`);
      setForm(data);
    } catch (e) {
      toast.error(formatApiError(e));
      onOpenChange?.(false);
    } finally {
      setLoading(false);
    }
  }, [clienteId, onOpenChange]);

  useEffect(() => {
    if (open && clienteId && podeEditar) carregar();
    if (!open) setForm(null);
  }, [open, clienteId, podeEditar, carregar]);

  // Foca o primeiro campo que a cobrança está pedindo — o usuário cai
  // direto no que precisa corrigir.
  useEffect(() => {
    if (form && primeiroFaltanteRef.current) primeiroFaltanteRef.current.focus();
  }, [form]);

  const salvar = async () => {
    setSaving(true);
    try {
      // PUT /clients/{id} grava o cadastro INTEIRO (ClientIn), então o
      // corpo sai do documento carregado com as alterações aplicadas por
      // cima — nunca um payload parcial que apagaria o resto do cadastro.
      const { data } = await api.put(`/clients/${clienteId}`, form);
      toast.success("Cadastro do cliente atualizado.");
      onOpenChange?.(false);
      // Quem chamou revalida e decide o que fazer — NUNCA emite sozinho.
      onSaved?.(data);
    } catch (e) {
      // Falha no save mantém este diálogo ABERTO (e, por consequência, o
      // modal de pagamento por baixo intacto).
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const semPermissao = open && !podeEditar;

  return (
    <Dialog open={!!open} onOpenChange={(v) => !v && onOpenChange?.(false)}>
      <DialogContent className="max-w-2xl" data-testid="cliente-edit-dialog">
        <DialogHeader>
          <DialogTitle>Editar cliente</DialogTitle>
        </DialogHeader>

        {mensagem && (
          <div className="flex gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3"
               data-testid="cliente-edit-contexto">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span>{mensagem}</span>
          </div>
        )}

        {semPermissao ? (
          <p className="text-sm text-slate-600" data-testid="cliente-edit-sem-permissao">
            Você não tem permissão para editar o cadastro de clientes. Peça a um administrador para
            completar os dados deste cliente e tente emitir a cobrança novamente.
          </p>
        ) : loading || !form ? (
          <div className="text-sm text-slate-400">Carregando...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[55vh] overflow-y-auto pr-1">
              {CAMPOS.map(({ campo, label }, i) => {
                const destacar = faltante(campo);
                const primeiro = destacar && faltantes[0] === campo;
                return (
                  <div key={campo}>
                    <Label className={destacar ? "text-red-700" : undefined}>
                      {label}{destacar && " *"}
                    </Label>
                    <Input
                      ref={primeiro ? primeiroFaltanteRef : undefined}
                      value={form[campo] ?? ""}
                      onChange={(e) => setForm((f) => ({ ...f, [campo]: e.target.value }))}
                      aria-invalid={destacar ? "true" : undefined}
                      className={destacar ? "border-red-500 focus-visible:ring-red-500" : undefined}
                      data-testid={`cliente-edit-${campo}`}
                      autoFocus={primeiro || (i === 0 && faltantes.length === 0)}
                    />
                    {destacar && (
                      <p className="text-xs text-red-600 mt-1" data-testid={`cliente-edit-erro-${campo}`}>
                        {label} é necessário para emitir esta cobrança.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="cliente-edit-cancelar">
                Cancelar
              </Button>
              <Button
                onClick={salvar} disabled={saving}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
                data-testid="cliente-edit-salvar"
              >
                <Save size={16} /> {saving ? "Salvando..." : "Salvar cliente"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
